import {
    ApolloError,
    DataProxy,
    useLazyQuery,
    useMutation,
    useQuery,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { Assignment } from '@deltek/specification-client/core/Models/assignment'
import { FirmResponse } from '@deltek/specification-client/core/Models/firm'
import { GetResultsInput } from '@deltek/specification-client/core/Models/projectItem'
import { ProjectTeam } from '@deltek/specification-client/core/Models/projectTeam'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { ProjectTeamInput } from '@deltek/specification-client/core/models/ProjectTeamInput'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { AllProjectResponse } from 'Models/project'
import { UpdateProjectTeamMutation } from 'api/graphQL/mutations/project.mutations'
import { FirmUsersProjectMembershipCountQuery } from 'api/graphQL/queries/firm.queries'
import { GetProjectTeamQuery } from 'api/graphQL/queries/project.queries'
import { MutationProjectTeamResponse } from 'api/graphQL/types/project.types'
import clsx from 'clsx'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import Slider from 'react-slick'
import { sortByProperty } from 'shared/utils/projectTeamUtils'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { getLocalStorageValue } from '../../../projectUtils'
import { mapFilterKvpToFiltersModel } from '../../projectOverviewUtils'
import ManageProjectTeamPopup from '../ManageProjectTeamPopup'
import Popout from './../../../ProjectsHomePage/components/Popout'
import CarouselCard from './CarouselCard'
import ProjectTeamMembersList from './ProjectTeamMembersList'
import { SlickArrowLeft, SlickArrowRight } from './SlickArrow'

const useStyles = makeStyles((theme: Theme) => ({
    sliderClass: {
        '&.slick-slider': {
            margin: `${theme.spacing(3.75)}px auto ${theme.spacing(3.75)}px`,
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5),
        },
        '& .slick-arrow': {
            fontSize: '4rem',
        },
        '& .slick-arrow.slick-prev': {
            width: '1em',
            height: '1em',
            fill: 'rgba(255, 255, 255, 0.54)',
            left: theme.spacing(1),
        },
        '& .slick-arrow.slick-next': {
            width: '1em',
            height: '1em',
            fill: 'rgba(255, 255, 255, 0.54)',
            right: 0,
        },
    },
    menuItem: {
        color: theme.palette.common.white,
        cursor: 'pointer',
        transition: '200ms',
        padding: theme.spacing(1, 1),
        marginLeft: -theme.spacing(1),
        marginRight: -theme.spacing(1),
        '&:hover': {
            borderRadius: 4,
            backgroundColor: 'rgba(0,0,0,0.2)',
        },
    },
    disableMenuItem: {
        color: '#969390',
        padding: theme.spacing(1, 1),
        marginLeft: -theme.spacing(1),
        marginRight: -theme.spacing(1),
        cursor: 'default',
    },
    projectTeamPopupCont: {
        '&.projectTeamPopout.dark': {
            width: 340,
        },
    },
    projectTeamPopupContListMode: {
        '&.projectTeamPopout.dark': {
            minHeight: 357,
            paddingTop: 11,
            paddingLeft: 13,
            paddingBottom: 0,
        },
    },
}))

const localStorageItemKey = (userId: string) =>
    `workspaceProgressTable${userId}`

interface IdNameProps {
    id: string
    name: string
    roles: string[]
}

interface ProfessionalTeamGridViewProps {
    professionalDisciplines?: Partial<IdNameProps>[]
    firmId: string
    productId?: string
    variant?: 'readOnly' | 'full'
    getProjectElements: (options?: any) => void
    userId?: string
    localStorageKey?: string
}

const ProjectTeamGridView: React.FC<ProfessionalTeamGridViewProps> = ({
    professionalDisciplines,
    firmId = '',
    productId,
    variant,
    getProjectElements,
    userId = '',
    localStorageKey = '',
}) => {
    const classes = useStyles()
    const [
        projectPopoutAnchor,
        setProjectPopoutAnchor,
    ] = React.useState<null | Element>(null)

    const [
        projectTeamActionMenu,
        setProjectTeamActionMenu,
    ] = React.useState<null | Element>(null)

    const [selectedAssignment, setSelectedAssignment] = React.useState<Partial<
        Assignment
    > | null>(null)
    const [toggler, setToggler] = React.useState<boolean>(false)

    const [projectTeam, setProjectTeam] = React.useState<null | string>(null)
    const [projectTeamUserList, setProjectTeamUserList] = React.useState<
        Partial<Assignment>[]
    >([])

    const [openProjectTeamDialog, setOpenProjectTeamDialog] = React.useState<
        boolean
    >(false)

    const [dialogVariant, setDialogVariant] = React.useState<'add' | 'remove'>(
        'add'
    )

    const { data: projectTeamData, loading: projectTeamDataLoading } = useQuery<
        AllProjectResponse
    >(GetProjectTeamQuery, {
        variables: {
            projectId: productId ?? '',
        },
    })

    const [getDisciplineData, { data: disciplineData }] = useLazyQuery<
        FirmResponse
    >(FirmUsersProjectMembershipCountQuery, {
        fetchPolicy: 'cache-and-network',
    })

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [updateProjectTeam] = useMutation<
        { updateProjectTeam: Partial<ProjectTeam> },
        { updateProjectTeamInput: ProjectTeamInput }
    >(UpdateProjectTeamMutation, {
        onError: (errors: ApolloError) => {
            showGlobalSnackbar({
                variables: {
                    message: errors.message,
                    variant: 'error',
                    id: `DashboardPage_SavedSearchError`,
                },
            })
        },
        onCompleted: () => {
            const saved = getLocalStorageValue(
                localStorageItemKey(userId),
                userId,
                '',
                localStorageKey
            )
            const pastItems = saved !== '' ? JSON.parse(saved as string) : {}
            const mappedFilters = mapFilterKvpToFiltersModel(
                pastItems.filters ?? []
            )
            getProjectElements({
                variables: {
                    input: {
                        projectId: productId ?? '',
                        limit: 100,
                        sortOrders: [
                            {
                                sortField: 'lastUpdated',
                                sortOrder: 'desc',
                            },
                        ],
                        filters: mappedFilters,
                    },
                },
            })
        },
    })

    const handleDialogClose = () => {
        setOpenProjectTeamDialog(false)
    }

    const toggleDialog = (open: boolean, variant: 'add' | 'remove') => () => {
        if (
            (variant === 'remove' && projectTeamUserList?.length > 0) ||
            (variant === 'add' &&
                projectTeam &&
                disciplineData?.firm?.usersWithProjectMembership?.count > 0)
        ) {
            setOpenProjectTeamDialog(open)
            setDialogVariant(variant)
            handleActionPopoutClose()
        }
    }

    const handlePageIconClick = (
        event: React.MouseEvent,
        currentProjectTeamName: string
    ) => {
        if (projectPopoutAnchor) {
            handlePopoutClose()
        } else {
            setProjectPopoutAnchor?.(event.currentTarget)
        }
        setProjectTeam(currentProjectTeamName ?? null)
    }

    const handlePopoutClose = () => {
        setProjectPopoutAnchor?.(null)
        setSelectedAssignment(null)
        setToggler(!toggler)
    }

    const handleCloseDetailPopup = () => {
        setSelectedAssignment(null)
        setToggler(!toggler)
    }

    const handleChangeAssignment = (assignment: Partial<Assignment> | null) => {
        setSelectedAssignment(assignment)
        setToggler(!toggler)
    }

    const handleActionPopoutClose = () => {
        setProjectTeamActionMenu(null)
    }

    const handleEllipsisIconClick = (event: React.MouseEvent) => {
        if (projectTeamActionMenu) {
            handleActionPopoutClose()
        } else {
            setProjectTeamActionMenu(event.currentTarget)
        }
    }

    const handleTeamMemberDelete = React.useCallback(
        (projectTeamMember: Partial<Assignment>) => {
            const updateProjectTeamInput: ProjectTeamInput = {
                id: projectTeamData?.getProject?.projectTeam?.id ?? '',
                projectId: productId,
                firmId: firmId ?? '',
                assignments: [],
            }
            const updatedAssignments = projectTeamData?.getProject.projectTeam?.assignments?.filter(
                (item: Partial<Assignment>) =>
                    item.user?.id !== projectTeamMember?.user?.id
            )
            if (updatedAssignments?.length) {
                updatedAssignments.forEach(
                    (assignmentItem: Partial<Assignment>) => {
                        updateProjectTeamInput.assignments.push({
                            description: assignmentItem?.description!,
                            firmId: firmId ?? '',
                            projectRole: assignmentItem?.projectRole!,
                            userId: assignmentItem?.user?.id,
                        })
                    }
                )
            }
            updateProjectTeam({
                variables: { updateProjectTeamInput },
                update(cache: DataProxy, result: MutationProjectTeamResponse) {
                    if (!!result.data) {
                        const getProjectOldData: any = cache.readQuery<
                            AllProjectResponse
                        >({
                            query: GetProjectTeamQuery,
                            variables: {
                                projectId: productId,
                            },
                        })

                        cache.writeQuery({
                            query: GetProjectTeamQuery,
                            variables: {
                                projectId: productId,
                            },
                            data: {
                                getProject: {
                                    ...getProjectOldData?.getProject,
                                    projectTeam: {
                                        ...getProjectOldData?.getProject
                                            .projectTeam,
                                        assignments:
                                            getProjectOldData?.getProject.projectTeam?.assignments?.filter(
                                                (item: Partial<Assignment>) =>
                                                    item.user?.id !==
                                                    projectTeamMember?.user?.id
                                            ) ?? [],
                                    },
                                },
                            },
                        })
                    }
                },
            })
        },
        [updateProjectTeam, projectTeamData, firmId, productId]
    )

    const settings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 7,
        prevArrow: <SlickArrowLeft />,
        nextArrow: <SlickArrowRight />,
        responsive: [
            {
                breakpoint: 1366,
                settings: {
                    slidesToShow: 6,
                },
            },
            {
                breakpoint: 1280,
                settings: {
                    slidesToShow: 5,
                },
            },
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 4,
                },
            },
            {
                breakpoint: 740,
                settings: {
                    slidesToShow: 3,
                },
            },
            {
                breakpoint: 575,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 375,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    }

    React.useEffect(() => {
        if (
            !!projectTeamData?.getProject?.projectTeam?.assignments &&
            projectTeam
        ) {
            let usersList: Partial<Assignment>[] = []
            usersList = projectTeamData?.getProject?.projectTeam?.assignments?.filter(
                (assignmentDetail: Partial<Assignment>) => {
                    return (
                        assignmentDetail.user?.profileInfo?.professionalDiscipline?.toLowerCase() ===
                        projectTeam?.toLowerCase()
                    )
                }
            )
            usersList = usersList.sort(sortByProperty(usersList))
            setProjectTeamUserList(usersList)
        }
    }, [projectTeam, projectTeamData])

    React.useEffect(() => {
        if (projectTeam || projectTeamData) {
            getDisciplineData({
                variables: {
                    firmId: firmId ?? '',
                    projectId: productId ?? '',
                    resultInput: {
                        filters: [
                            {
                                itemField: 'isAssignedToProject',
                                filterValues: ['false'],
                            },
                            {
                                itemField: 'discipline',
                                filterValues: [projectTeam],
                            },
                        ],
                    } as Partial<GetResultsInput>,
                },
            })
        }
    }, [projectTeam, projectTeamData, firmId, getDisciplineData, productId])

    return (
        <>
            {(!projectTeamDataLoading || projectTeamData) && (
                <div>
                    <Slider
                        className={clsx(
                            classes.sliderClass,
                            'projectTeamCarousel'
                        )}
                        {...settings}
                    >
                        {professionalDisciplines?.map((item, index) => {
                            return (
                                <CarouselCard
                                    handlePageIconClick={handlePageIconClick}
                                    handleEllipsisIconClick={
                                        handleEllipsisIconClick
                                    }
                                    projectTeamName={item?.name ?? ''}
                                    setProjectTeam={setProjectTeam}
                                    key={index}
                                    variant={variant}
                                />
                            )
                        })}
                    </Slider>
                </div>
            )}
            <Popout
                placement="right"
                className={clsx(
                    classes.projectTeamPopupCont,
                    'projectTeamPopout',
                    !selectedAssignment ?? classes.projectTeamPopupContListMode
                )}
                anchorEl={projectPopoutAnchor}
                onCloseRequest={handlePopoutClose}
                theme="dark"
            >
                <ProjectTeamMembersList
                    projectTeamUserList={projectTeamUserList}
                    projectTeam={projectTeam}
                    selectedAssignment={selectedAssignment}
                    setSelectedAssignment={handleChangeAssignment}
                    setOpenProjectTeamDialog={toggleDialog}
                    handleTeamMemberDelete={handleTeamMemberDelete}
                    handlePopoutClose={handleCloseDetailPopup}
                    firmId={firmId}
                    projectId={productId!}
                    variant={variant}
                    professionalDisciplines={professionalDisciplines}
                    setContentInEditMode={() => setToggler(!toggler)}
                    disciplineMemberCount={
                        projectTeam &&
                        disciplineData?.firm?.usersWithProjectMembership?.count
                    }
                />
            </Popout>
            <Popout
                placement="right"
                className="projectTeamPopout"
                anchorEl={projectTeamActionMenu}
                onCloseRequest={handleActionPopoutClose}
                theme="dark"
            >
                <GridContainer
                    direction="column"
                    data-testid="teamActionsContainer"
                >
                    <GridItem
                        className={
                            disciplineData?.firm?.usersWithProjectMembership
                                ?.count > 0
                                ? classes.menuItem
                                : classes.disableMenuItem
                        }
                        onClick={toggleDialog(true, 'add')}
                        onKeyDown={onEnterKeyDown(toggleDialog(true, 'add'))}
                        data-testid="projectTeamAddToTeamButton"
                        tabIndex={
                            disciplineData?.firm?.usersWithProjectMembership
                                ?.count > 0
                                ? 0
                                : -1
                        }
                    >
                        <Typography
                            data-testid="addTeamMemberButton"
                            variant="caption"
                            component="span"
                        >
                            Add Team Member
                        </Typography>
                    </GridItem>
                    <GridItem
                        className={
                            projectTeamUserList?.length
                                ? classes.menuItem
                                : classes.disableMenuItem
                        }
                        onClick={toggleDialog(true, 'remove')}
                        onKeyDown={onEnterKeyDown(toggleDialog(true, 'remove'))}
                        data-testid="projectTeamRemoveFromTeamButton"
                        tabIndex={projectTeamUserList?.length ? 0 : -1}
                    >
                        <Typography variant="caption" component="span">
                            Remove Team Member
                        </Typography>
                    </GridItem>
                </GridContainer>
            </Popout>
            {openProjectTeamDialog && (
                <ManageProjectTeamPopup
                    projectId={productId!}
                    open={openProjectTeamDialog}
                    handleDialogClose={handleDialogClose}
                    variant={dialogVariant}
                    projectTeam={projectTeam}
                />
            )}
        </>
    )
}

export default ProjectTeamGridView
